import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../AuthContext';
import InvoiceScanner from '../../components/InvoiceScanner';
import ApprovalGateway from '../../components/ApprovalGateway';
import InventoryPage from '../../components/InventoryPage';
import VoiceInterface from '../../components/VoiceInterface';
import SettingsView from './SettingsView';
import InvoiceHistory from '../../components/InvoiceHistory';
import PurchaseOrders from '../../components/PurchaseOrders';
import SalesEntry from '../../components/SalesEntry';
import ReportsView from './ReportsView';
import MenuManager from '../../components/MenuManager';
import OpsDashboard from '../../components/OpsDashboard';
import CoversImportView from './CoversImportView';

function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ below_par: 0, pending_orders: 0, vendors: 0, users: 0 });

  useEffect(() => {
    Promise.all([
      api.get('/inventory?below_par=true'),
      api.get('/orders?status=draft'),
      api.get('/vendors'),
      api.get('/users'),
    ]).then(([inv, orders, vendors, users]) => {
      setStats({
        below_par: inv.data.length,
        pending_orders: orders.data.length,
        vendors: vendors.data.length,
        users: users.data.length,
      });
    }).catch(() => {});
  }, []);

  const tiles = [
    { path: '/admin/inventory', icon: '📦', label: 'Inventory', color: '#0d9488' },
    { path: '/admin/orders', icon: '✅', label: 'Orders', color: '#f59e0b', badge: stats.pending_orders },
    { path: '/admin/vendors', icon: '🌾', label: 'Vendors', color: '#3b82f6' },
    { path: '/admin/purchase-orders', icon: '📋', label: 'Purchase Orders', color: '#0ea5e9' },
    { path: '/admin/users', icon: '👥', label: 'Users', color: '#8b5cf6' },
    { path: '/admin/scan', icon: '📷', label: 'Scan Invoice', color: '#ec4899' },
    { path: '/admin/voice', icon: '🎙️', label: 'Voice', color: '#6366f1' },
    { path: '/admin/invoices', icon: '🧾', label: 'Invoices', color: '#f97316' },
    { path: '/admin/sales', icon: '📊', label: 'Sales', color: '#10b981' },
    { path: '/admin/reports', icon: '📈', label: 'Reports', color: '#8b5cf6' },
    { path: '/admin/covers', icon: '👥', label: 'Covers & Servers', color: '#14b8a6' },
    { path: '/admin/menu', icon: '🍽️', label: 'Menu', color: '#a78bfa' },
  ];

  return (
    <>
      <div className="page-header">
        <span className="page-title">Admin Dashboard</span>
        <span className="text-sm text-gray">{user?.name} · {user?.role}</span>
      </div>
      <div className="page-body">
        <div className="stat-grid">
          <div className={`stat-card ${stats.below_par > 0 ? 'danger' : 'success'}`}>
            <div className="stat-label">Below Par</div>
            <div className="stat-value">{stats.below_par}</div>
          </div>
          <div className={`stat-card ${stats.pending_orders > 0 ? 'warning' : ''}`}>
            <div className="stat-label">Pending Orders</div>
            <div className="stat-value">{stats.pending_orders}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Vendors</div>
            <div className="stat-value">{stats.vendors}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Users</div>
            <div className="stat-value">{stats.users}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 8 }}>
          {tiles.map(t => (
            <button key={t.path} className="card" style={{ border: 'none', cursor: 'pointer', padding: 24, textAlign: 'center', position: 'relative' }} onClick={() => navigate(t.path)}>
              {t.badge > 0 && (
                <span style={{ position: 'absolute', top: 8, right: 8, background: '#ef4444', color: 'white', borderRadius: '999px', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{t.badge}</span>
              )}
              <div style={{ fontSize: 36, marginBottom: 8 }}>{t.icon}</div>
              <div className="font-semibold">{t.label}</div>
            </button>
          ))}
        </div>

        <div style={{ marginTop: 24 }}>
          <WeatherPredictions />
        </div>
      </div>
    </>
  );
}

function VendorsPanel() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', type: 'farmer', contact_phone: '', contact_email: '', is_local: false });
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/vendors').then(({ data }) => setVendors(data)).finally(() => setLoading(false));
  }, []);

  function flash(m) { setMsg(m); setTimeout(() => setMsg(''), 3000); }

  async function addVendor(e) {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await api.post('/vendors', form);
      setVendors(prev => [data, ...prev]);
      setShowForm(false);
      setForm({ name: '', type: 'farmer', contact_phone: '', contact_email: '', is_local: false });
      flash('Vendor added!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    } finally { setAdding(false); }
  }

  async function saveEdit(id) {
    try {
      const { data } = await api.put(`/vendors/${id}`, editForm);
      setVendors(prev => prev.map(v => v.id === id ? data : v));
      setEditId(null);
      flash('Updated!');
    } catch { alert('Failed to save'); }
  }

  async function deleteVendor(id) {
    try {
      await api.delete(`/vendors/${id}`);
      setVendors(prev => prev.filter(v => v.id !== id));
      setDeleteConfirm(null);
      flash('Vendor removed');
    } catch { alert('Failed to delete'); }
  }

  if (loading) return <div className="loading">Loading vendors...</div>;

  const TYPES = ['farmer', 'fisherman', 'distributor', 'other'];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold">All Vendors ({vendors.length})</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>+ Add Vendor</button>
      </div>

      {msg && <div className="alert alert-success mb-3">{msg}</div>}

      {showForm && (
        <div className="card mb-4">
          <div className="card-header">New Vendor</div>
          <div className="card-body">
            <form onSubmit={addVendor}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
                    {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} />
                </div>
              </div>
              <label className="flex items-center gap-2 mb-4" style={{ cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.is_local} onChange={e => setForm({...form, is_local: e.target.checked})} />
                Local vendor (eligible for SMS availability queries)
              </label>
              <div className="flex gap-2">
                <button className="btn btn-primary btn-sm" type="submit" disabled={adding}>{adding ? 'Adding...' : 'Add Vendor'}</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Type</th><th>Phone</th><th>Email</th><th>Local</th><th>Actions</th></tr></thead>
            <tbody>
              {vendors.map(v => (
                <tr key={v.id}>
                  {editId === v.id ? (
                    <>
                      <td><input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} style={{ fontSize: 13, width: '100%' }} /></td>
                      <td>
                        <select value={editForm.type} onChange={e => setEditForm({...editForm, type: e.target.value})} style={{ fontSize: 12 }}>
                          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td><input value={editForm.contact_phone || ''} onChange={e => setEditForm({...editForm, contact_phone: e.target.value})} style={{ fontSize: 13, width: '100%' }} /></td>
                      <td><input value={editForm.contact_email || ''} onChange={e => setEditForm({...editForm, contact_email: e.target.value})} style={{ fontSize: 13, width: '100%' }} /></td>
                      <td>
                        <input type="checkbox" checked={editForm.is_local} onChange={e => setEditForm({...editForm, is_local: e.target.checked})} />
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={() => saveEdit(v.id)}>Save</button>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setEditId(null)}>Cancel</button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td><strong>{v.name}</strong></td>
                      <td><span className="badge badge-gray">{v.type}</span></td>
                      <td className="text-sm">{v.contact_phone || '—'}</td>
                      <td className="text-sm text-gray">{v.contact_email || '—'}</td>
                      <td>{v.is_local ? <span className="badge badge-teal">🌱 Local</span> : <span className="badge badge-gray">Mainland</span>}</td>
                      <td>
                        {deleteConfirm === v.id ? (
                          <div className="flex gap-1 items-center">
                            <span style={{ fontSize: 11, color: '#ef4444' }}>Delete?</span>
                            <button className="btn btn-sm" style={{ fontSize: 11, background: '#ef4444', color: '#fff', padding: '2px 6px' }} onClick={() => deleteVendor(v.id)}>Yes</button>
                            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setDeleteConfirm(null)}>No</button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => { setEditId(v.id); setEditForm({ name: v.name, type: v.type, contact_phone: v.contact_phone, contact_email: v.contact_email, is_local: v.is_local }); }}>✏️ Edit</button>
                            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: '#ef4444' }} onClick={() => setDeleteConfirm(v.id)}>🗑️</button>
                          </div>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [resetId, setResetId] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const ROLES = ['staff', 'chef', 'bartender', 'supplies', 'gm', 'restaurant_admin'];

  useEffect(() => {
    api.get('/users').then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  }, []);

  function flash(m, isErr = false) {
    isErr ? setError(m) : setMsg(m);
    setTimeout(() => { setMsg(''); setError(''); }, 3500);
  }

  async function addUser(e) {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await api.post('/users', form);
      setUsers(prev => [data, ...prev]);
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: 'staff' });
      flash('User created!');
    } catch (err) {
      flash(err.response?.data?.error || 'Failed to create user', true);
    } finally { setAdding(false); }
  }

  async function saveRole(userId) {
    try {
      const { data } = await api.put(`/users/${userId}`, { role: editRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: data.role } : u));
      setEditId(null);
      flash('Role updated');
    } catch { flash('Failed to update role', true); }
  }

  async function toggleActive(user) {
    try {
      await api.put(`/users/${user.id}`, { active: !user.active });
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, active: !u.active } : u));
      flash(`${user.name} ${!user.active ? 'activated' : 'deactivated'}`);
    } catch { flash('Failed to update status', true); }
  }

  async function resetPassword(userId) {
    if (!newPw || newPw.length < 8) { flash('Password must be 8+ characters', true); return; }
    try {
      await api.post(`/users/${userId}/reset-password`, { new_password: newPw });
      setResetId(null); setNewPw('');
      flash('Password updated');
    } catch (err) { flash(err.response?.data?.error || 'Failed', true); }
  }

  if (loading) return <div className="loading">Loading users...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold">Team Members ({users.length})</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>+ Add User</button>
      </div>

      {msg && <div className="alert alert-success mb-3">{msg}</div>}
      {error && <div className="alert alert-error mb-3">{error}</div>}

      {showAdd && (
        <div className="card mb-4">
          <div className="card-header">New Team Member</div>
          <div className="card-body">
            <form onSubmit={addUser}>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} minLength={8} required />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                    {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <button className="btn btn-primary btn-sm" type="submit" disabled={adding}>{adding ? 'Creating...' : 'Create User'}</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td><strong>{u.name}</strong></td>
                  <td className="text-sm text-gray">{u.email}</td>
                  <td>
                    {editId === u.id ? (
                      <div className="flex items-center gap-1">
                        <select value={editRole} onChange={e => setEditRole(e.target.value)} style={{ fontSize: 12, padding: '2px 4px' }}>
                          {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                        </select>
                        <button className="btn btn-primary btn-sm" style={{ padding: '2px 6px', fontSize: 11 }} onClick={() => saveRole(u.id)}>✓</button>
                        <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 11 }} onClick={() => setEditId(null)}>✕</button>
                      </div>
                    ) : (
                      <span className="badge badge-blue" style={{ cursor: 'pointer' }} onClick={() => { setEditId(u.id); setEditRole(u.role); }} title="Click to edit role">
                        {u.role?.replace('_', ' ')}
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      className={`badge ${u.active ? 'badge-green' : 'badge-red'}`}
                      style={{ cursor: 'pointer', border: 'none', background: 'transparent' }}
                      onClick={() => toggleActive(u)}
                      title="Click to toggle active/inactive"
                    >
                      {u.active ? '● Active' : '○ Inactive'}
                    </button>
                  </td>
                  <td>
                    {resetId === u.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 200 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', marginBottom: 2 }}>
                          🔑 Resetting password for <span style={{ color: '#fff' }}>{u.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <input type="password" placeholder="New password (8+ chars)" value={newPw} onChange={e => setNewPw(e.target.value)} style={{ fontSize: 12, width: 160, padding: '2px 6px' }} />
                          <button className="btn btn-primary btn-sm" style={{ padding: '2px 6px', fontSize: 11 }} onClick={() => resetPassword(u.id)}>Set</button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 11 }} onClick={() => { setResetId(null); setNewPw(''); }}>✕</button>
                        </div>
                      </div>
                    ) : (
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setResetId(u.id)}>🔑 Reset PW</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AdminView() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route index element={<OpsDashboard basePath="/admin" />} />
      <Route path="inventory" element={<InventoryPage title="Inventory" />} />
      <Route path="orders" element={<><div className="page-header"><span className="page-title">Orders</span></div><div className="page-body"><ApprovalGateway /></div></>} />
      <Route path="vendors" element={<><div className="page-header"><span className="page-title">Vendors</span></div><div className="page-body"><VendorsPanel /></div></>} />
      <Route path="users" element={<><div className="page-header"><span className="page-title">Users</span></div><div className="page-body"><UsersPanel /></div></>} />
      <Route path="scan" element={<><div className="page-header"><span className="page-title">Scan Invoice</span></div><div className="page-body"><InvoiceScanner /></div></>} />
      <Route path="voice" element={<><div className="page-header"><span className="page-title">Voice</span></div><div className="page-body"><VoiceInterface restaurantId={user?.restaurant_id} /></div></>} />
      <Route path="settings" element={<><div className="page-header"><span className="page-title">Settings</span></div><div className="page-body"><SettingsView /></div></>} />
      <Route path="purchase-orders" element={<><div className="page-header"><span className="page-title">Purchase Orders</span></div><div className="page-body"><PurchaseOrders /></div></>} />
      <Route path="invoices" element={<><div className="page-header"><span className="page-title">Invoice History</span></div><div className="page-body"><InvoiceHistory /></div></>} />
      <Route path="sales" element={<><div className="page-header"><span className="page-title">Sales Entry</span></div><div className="page-body"><SalesEntry /></div></>} />
      <Route path="reports" element={<><div className="page-header"><span className="page-title">Reports</span></div><div className="page-body"><ReportsView /></div></>} />
      <Route path="menu" element={<MenuManager />} />
      <Route path="covers" element={<CoversImportView />} />
      {/* Legacy redirect */}
      <Route path="covers-import" element={<CoversImportView />} />
    </Routes>
  );
}
